'use client';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  updateProfile,
  updatePassword,
  updateSignature,
  updateSeal,
  generateApiKey,
  deleteAccount,
  setup2FA,
  verify2FA,
  disable2FA
} from '../redux/authSlice';
import {
  User, Lock, Shield, Key, Bell, CreditCard, Trash2,
  CheckCircle, AlertCircle, Eye, EyeOff, Loader, Smartphone, Globe,
  RefreshCw, Settings as SettingsIcon, LogOut, Mail, Building,
  ChevronRight, Activity, Zap, Fingerprint, ShieldCheck, Cpu,
  Copy, Info, Server, Database, Code, Crosshair, Clock, X, QrCode, MessageSquare, Cloud, Save
} from 'lucide-react';
import { Pencil, Trash } from 'lucide-react';
import DashboardModuleShell from '../components/DashboardModuleShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Toggle } from '../components/ui/Toggle';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import alertService from '../utils/alert';
import { cn } from '../utils/cn';
import api from '../services/api';
import { SUPPORTED_CURRENCIES } from '../constants/currencies';
import { CountrySelect, StateSelect } from '../components/CountryStateSelect';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

const COMMON_TIMEZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Australia/Sydney', 'UTC',
];

function getSupportedTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (e) {
    // fall through to the short hardcoded list below
  }
  return COMMON_TIMEZONES;
}

export default function SettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading } = useSelector(state => state.auth);
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (activeTab !== 'usage') return;
    api.get('/billing/usage').then(({ data }) => setUsage(data.data)).catch(() => {});
  }, [activeTab]);
  const [profileData, setProfileData] = useState({
    companyName: user?.companyName || '',
    phoneNumber: user?.phoneNumber || '',
    website: user?.website || '',
    country: user?.country || '',
    state: user?.state || '',
    address: user?.address || '',
    pinCode: user?.pinCode || '',
    gstNumber: user?.gstNumber || '',
    stateCode: user?.stateCode || '',
    pan: user?.pan || '',
    tan: user?.tan || '',
    cin: user?.cin || '',
    msmeNumber: user?.msmeNumber || '',
    iecNumber: user?.iecNumber || '',
    fssaiNumber: user?.fssaiNumber || '',
    udyamNumber: user?.udyamNumber || '',
    timezone: user?.timezone || 'Asia/Kolkata',
    baseCurrency: user?.baseCurrency || 'INR',
    imagekitUrlEndpoint: user?.imagekitUrlEndpoint || '',
    imagekitPublicKey: user?.imagekitPublicKey || '',
    imagekitPrivateKey: '', // Kept empty for security
    invoiceFooterNote: user?.invoiceFooterNote || '',
    bank_accountName: user?.bankDetails?.accountName || '',
    bank_accountNumber: user?.bankDetails?.accountNumber || '',
    bank_bankName: user?.bankDetails?.bankName || '',
    bank_ifscCode: user?.bankDetails?.ifscCode || '',
    bank_branch: user?.bankDetails?.branch || '',
    invoiceSettings: {
      prefix: user?.invoiceSettings?.prefix ?? 'CINV-',
      suffix: user?.invoiceSettings?.suffix ?? '',
      financialYearFormat: user?.invoiceSettings?.financialYearFormat || 'calendar',
      startingNumber: user?.invoiceSettings?.startingNumber ?? 1,
      numberPadding: user?.invoiceSettings?.numberPadding ?? 4,
      creditNotePrefix: user?.invoiceSettings?.creditNotePrefix ?? 'CN-',
      debitNotePrefix: user?.invoiceSettings?.debitNotePrefix ?? 'DN-',
      proformaPrefix: user?.invoiceSettings?.proformaPrefix ?? 'PRO-',
      purchasePrefix: user?.invoiceSettings?.purchasePrefix ?? 'PUR-',
      deliveryChallanPrefix: user?.invoiceSettings?.deliveryChallanPrefix ?? 'DC-',
      salesOrderPrefix: user?.invoiceSettings?.salesOrderPrefix ?? 'SO-',
      whatsappTemplate: {
        name: user?.invoiceSettings?.whatsappTemplate?.name ?? '',
        language: user?.invoiceSettings?.whatsappTemplate?.language ?? 'en',
      },
    },
    invoiceTerms: {
      paymentDueDays: user?.invoiceTerms?.paymentDueDays ?? 30,
      latePaymentNote: user?.invoiceTerms?.latePaymentNote || '',
      warrantyTerms: user?.invoiceTerms?.warrantyTerms || '',
      returnPolicy: user?.invoiceTerms?.returnPolicy || '',
      jurisdiction: user?.invoiceTerms?.jurisdiction || '',
    },
  });
  const [paymentMethods, setPaymentMethods] = useState(
    user?.paymentMethods?.length
      ? user.paymentMethods
      : [
        { name: 'Cash', isActive: true, isDefault: true },
        { name: 'UPI', isActive: true, isDefault: false },
        { name: 'Bank Transfer', isActive: true, isDefault: false },
        { name: 'Credit Card', isActive: true, isDefault: false },
        { name: 'Debit Card', isActive: true, isDefault: false },
        { name: 'Cheque', isActive: true, isDefault: false },
        { name: 'Wallet', isActive: true, isDefault: false },
      ]
  );
  const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [sealUploading, setSealUploading] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [logoUploading, setLogoUploading] = useState(false);
  // Approved Meta WhatsApp templates, for the "Invoice WhatsApp Template"
  // picker — sending an invoice outside Meta's 24h customer-session window
  // requires a business-initiated template message, and this is the only
  // place a tenant can configure which one to use.
  const [waTemplates, setWaTemplates] = useState([]);

  // Exchange rates (currency -> rateToBase), for converting non-base-currency
  // deal/lead values into the company's base currency on dashboards/reports.
  const [rates, setRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);

  useEffect(() => {
    api.get('/crm/currency/rates')
      .then(res => setRates(res.data.rates || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/meta/templates')
      .then(res => setWaTemplates((res.data.templates || []).filter(t => t.status === 'APPROVED')))
      .catch(() => {});
  }, []);

  const handleRateChange = (currency, value) => {
    setRates(prev => ({ ...prev, [currency]: value }));
  };

  const handleRateSave = async (currency) => {
    const rateToBase = parseFloat(rates[currency]);
    if (!rateToBase || rateToBase <= 0) {
      alertService.error('Invalid Rate', 'Enter a positive number.');
      return;
    }
    try {
      setRatesLoading(true);
      await api.put('/crm/currency/rates', { currency, rateToBase });
      alertService.toast('success', `Rate for ${currency} updated`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to update rate');
    } finally {
      setRatesLoading(false);
    }
  };

  // 2FA States
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState('initial'); // 'initial', 'qr', 'verify'
  const [qrCodeData, setQrCodeData] = useState({ secret: '', qrCodeUrl: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [isDisabling2FA, setIsDisabling2FA] = useState(false);

  const { hasAccess: hasAgenticMode, plan: agenticPlan } = useFeatureAccess('agentic_mode');
  const [apiKeyShow, setApiKeyShow] = useState(false);
  const [mcpKeys, setMcpKeys] = useState([]);
  const [mcpKeysLoading, setMcpKeysLoading] = useState(false);
  const [mcpKeyName, setMcpKeyName] = useState('');
  const [mcpKeyKind, setMcpKeyKind] = useState('mcp'); // 'mcp' = Claude/MCP (read + write), 'agent' = chat with the support agent over the API
  const [mcpKeyIps, setMcpKeyIps] = useState('');
  const [mcpKeyCreating, setMcpKeyCreating] = useState(false);
  const [newMcpKey, setNewMcpKey] = useState(null);

  // SMS Configuration State
  const [smsConfig, setSmsConfig] = useState(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTestLoading, setSmsTestLoading] = useState(false);
  const [smsFormData, setSmsFormData] = useState({
    smsEnabled: false,
    provider: 'msg91',
    apiKey: '',
    accountSid: '',
    fromNumber: '',
    senderId: '',
    route: '4',
  });

  // Email Configuration State
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  const [emailFormData, setEmailFormData] = useState({
    smtpEnabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: 'WaapLeads',
    smtpSecure: true,
    notificationsEnabled: true,
    notificationMode: 'instant',
    dailyDigestTime: '09:00',
    campaignCompletionEmails: [],
    attachReportPDF: true,
    attachDetailedCSV: true
  });

  const [newEmail, setNewEmail] = useState('');

  // Templates State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [activeTemplateType, setActiveTemplateType] = useState('email'); // 'email' or 'sms'
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const emailRes = await fetch('/api/settings/email-templates', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (emailRes.ok) {
        const data = await emailRes.json();
        setEmailTemplates(data.templates);
      } else {
        alertService.error('Error', 'Failed to fetch email templates');
      }

      const smsRes = await fetch('/api/settings/sms-templates', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (smsRes.ok) {
        const data = await smsRes.json();
        setSmsTemplates(data.templates);
      } else {
        alertService.error('Error', 'Failed to fetch SMS templates');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to fetch templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setTemplatesLoading(true);
    try {
      const endpoint = activeTemplateType === 'email' ? '/api/settings/email-templates' : '/api/settings/sms-templates';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingTemplate)
      });
      if (response.ok) {
        alertService.toast('success', 'Template saved successfully');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        alertService.error('Error', 'Failed to save template');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to save template');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateKey) => {
    const confirmed = await alertService.confirm('Delete override?', 'This will revert to the global system template.', 'warning', 'Delete');
    if (!confirmed) return;
    
    setTemplatesLoading(true);
    try {
      const endpoint = activeTemplateType === 'email' ? `/api/settings/email-templates/${templateKey}` : `/api/settings/sms-templates/${templateKey}`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        alertService.toast('success', 'Template override deleted');
        fetchTemplates();
      } else {
        alertService.error('Error', 'Failed to delete template');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to delete template');
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        companyName: user.companyName || '',
        phoneNumber: user.phoneNumber || '',
        website: user.website || '',
        country: user.country || '',
        state: user.state || '',
        address: user.address || '',
        pinCode: user.pinCode || '',
        gstNumber: user.gstNumber || '',
        stateCode: user.stateCode || '',
        pan: user.pan || '',
        tan: user.tan || '',
        cin: user.cin || '',
        msmeNumber: user.msmeNumber || '',
        iecNumber: user.iecNumber || '',
        fssaiNumber: user.fssaiNumber || '',
        udyamNumber: user.udyamNumber || '',
        timezone: user.timezone || 'Asia/Kolkata',
        baseCurrency: user.baseCurrency || 'INR',
        imagekitUrlEndpoint: user.imagekitUrlEndpoint || '',
        imagekitPublicKey: user.imagekitPublicKey || '',
        imagekitPrivateKey: '',
        invoiceFooterNote: user.invoiceFooterNote || '',
        bank_accountName: user.bankDetails?.accountName || '',
        bank_accountNumber: user.bankDetails?.accountNumber || '',
        bank_bankName: user.bankDetails?.bankName || '',
        bank_ifscCode: user.bankDetails?.ifscCode || '',
        bank_branch: user.bankDetails?.branch || '',
        invoiceSettings: {
          prefix: user.invoiceSettings?.prefix ?? 'CINV-',
          suffix: user.invoiceSettings?.suffix ?? '',
          financialYearFormat: user.invoiceSettings?.financialYearFormat || 'calendar',
          startingNumber: user.invoiceSettings?.startingNumber ?? 1,
          numberPadding: user.invoiceSettings?.numberPadding ?? 4,
          creditNotePrefix: user.invoiceSettings?.creditNotePrefix ?? 'CN-',
          debitNotePrefix: user.invoiceSettings?.debitNotePrefix ?? 'DN-',
          proformaPrefix: user.invoiceSettings?.proformaPrefix ?? 'PRO-',
          purchasePrefix: user.invoiceSettings?.purchasePrefix ?? 'PUR-',
          deliveryChallanPrefix: user.invoiceSettings?.deliveryChallanPrefix ?? 'DC-',
          salesOrderPrefix: user.invoiceSettings?.salesOrderPrefix ?? 'SO-',
          whatsappTemplate: {
            name: user.invoiceSettings?.whatsappTemplate?.name ?? '',
            language: user.invoiceSettings?.whatsappTemplate?.language ?? 'en',
          },
        },
        invoiceTerms: {
          paymentDueDays: user.invoiceTerms?.paymentDueDays ?? 30,
          latePaymentNote: user.invoiceTerms?.latePaymentNote || '',
          warrantyTerms: user.invoiceTerms?.warrantyTerms || '',
          returnPolicy: user.invoiceTerms?.returnPolicy || '',
          jurisdiction: user.invoiceTerms?.jurisdiction || '',
        },
      });
      if (user.paymentMethods?.length) setPaymentMethods(user.paymentMethods);
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    if (e) e.preventDefault();
    try {
      await dispatch(updateProfile({ ...profileData, paymentMethods })).unwrap();
      alertService.toast('success', 'Profile configuration synchronized successfully');
    } catch (err) {
      alertService.error('Update Failed', err || 'Could not update profile info.');
    }
  };

  const addPaymentMethod = () => {
    const name = newPaymentMethodName.trim();
    if (!name) return;
    if (paymentMethods.some(m => m.name.toLowerCase() === name.toLowerCase())) {
      return alertService.error('Duplicate', 'That payment method already exists.');
    }
    setPaymentMethods(prev => [...prev, { name, isActive: true, isDefault: false }]);
    setNewPaymentMethodName('');
  };

  const removePaymentMethod = (name) => {
    setPaymentMethods(prev => prev.filter(m => m.name !== name));
  };

  const togglePaymentMethodActive = (name) => {
    setPaymentMethods(prev => prev.map(m => (m.name === name ? { ...m, isActive: !m.isActive } : m)));
  };

  const setDefaultPaymentMethod = (name) => {
    setPaymentMethods(prev => prev.map(m => ({ ...m, isDefault: m.name === name })));
  };

  // Uploads immediately on selection, same pattern as handleLogoUpload above.
  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('signature', file);
    try {
      setSignatureUploading(true);
      await dispatch(updateSignature(formData)).unwrap();
      alertService.toast('success', 'Digital signature updated');
    } catch (err) {
      alertService.error('Upload Failed', err || 'Could not upload signature.');
    } finally {
      setSignatureUploading(false);
      e.target.value = '';
    }
  };

  const handleSealUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('seal', file);
    try {
      setSealUploading(true);
      await dispatch(updateSeal(formData)).unwrap();
      alertService.toast('success', 'Company seal updated');
    } catch (err) {
      alertService.error('Upload Failed', err || 'Could not upload seal.');
    } finally {
      setSealUploading(false);
      e.target.value = '';
    }
  };

  // Uploads immediately on selection (like the Storage page's file upload)
  // rather than waiting for "Save", since a logo isn't part of
  // the text form state and shouldn't require an unrelated field edit to save.
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    try {
      setLogoUploading(true);
      await dispatch(updateProfile(formData)).unwrap();
      alertService.toast('success', 'Company logo updated');
    } catch (err) {
      alertService.error('Upload Failed', err || 'Could not upload logo.');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handlePasswordUpdate = async (e) => {
    if (e) e.preventDefault();
    if (passwords.next !== passwords.confirm) {
      alertService.error('Validation Error', 'New passwords do not match.');
      return;
    }
    try {
      await dispatch(updatePassword({ currentPassword: passwords.current, newPassword: passwords.next })).unwrap();
      alertService.toast('success', 'Security credentials updated');
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (err) {
      alertService.error('Update Error', err || 'Verification of current credentials failed.');
    }
  };

  const handleGenerateKey = async () => {
    const confirmed = await alertService.confirm(
      'Regenerate API Key?',
      'Generating a new API key will immediately invalidate your current key. This action cannot be undone.',
      'warning',
      'Regenerate'
    );
    if (confirmed) {
      try {
        await dispatch(generateApiKey()).unwrap();
        alertService.toast('success', 'New API key generated successfully');
      } catch (err) {
        alertService.error('Generation Failed', err || 'Server communication error.');
      }
    }
  };

  const fetchMcpKeys = async () => {
    setMcpKeysLoading(true);
    try {
      const res = await api.get('/api-keys');
      setMcpKeys(res.data.data || []);
    } catch (err) {
      alertService.error('Load Failed', 'Could not load your Claude/MCP keys.');
    } finally {
      setMcpKeysLoading(false);
    }
  };

  const handleCreateMcpKey = async () => {
    if (!mcpKeyName.trim()) {
      alertService.error('Name Required', 'Give this key a name, e.g. "Claude Desktop".');
      return;
    }
    setMcpKeyCreating(true);
    try {
      const allowedIps = mcpKeyIps.split(/[\s,]+/).filter(Boolean);
      const res = await api.post('/api-keys', { name: mcpKeyName.trim(), scopes: mcpKeyKind === 'agent' ? ['agent'] : ['read', 'write'], ...(allowedIps.length ? { allowedIps } : {}) });
      setNewMcpKey(res.data.data.key);
      setMcpKeyName('');
      setMcpKeyIps('');
      await fetchMcpKeys();
    } catch (err) {
      alertService.error('Generation Failed', err?.response?.data?.error || 'Could not generate key.');
    } finally {
      setMcpKeyCreating(false);
    }
  };

  const handleRevokeMcpKey = async (id) => {
    const confirmed = await alertService.confirm(
      'Revoke this key?',
      'Any connected Claude/MCP client using this key will immediately lose access to your CRM.',
      'warning',
      'Revoke'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/api-keys/${id}`);
      alertService.toast('success', 'Key revoked');
      await fetchMcpKeys();
    } catch (err) {
      alertService.error('Revoke Failed', 'Could not revoke this key.');
    }
  };

  // 2FA Logic
  const handleStart2FA = async () => {
    try {
      const result = await dispatch(setup2FA()).unwrap();
      setQrCodeData(result);
      setTwoFactorStep('qr');
      setIs2FAModalOpen(true);
    } catch (err) {
      alertService.error('Setup Failed', err || 'Could not initiate 2FA setup.');
    }
  };

  const handleVerifyAndEnable2FA = async () => {
    if (verificationCode.length !== 6) return;
    try {
      await dispatch(verify2FA({ code: verificationCode })).unwrap();
      alertService.toast('success', '2FA enabled successfully');
      setIs2FAModalOpen(false);
      setTwoFactorStep('initial');
      setVerificationCode('');
    } catch (err) {
      alertService.error('Verification Failed', err || 'Invalid code. Please try again.');
    }
  };

  const handleDisable2FA = async () => {
    const confirmed = await alertService.confirm(
      'Disable 2FA?',
      'This will reduce your account security. You will need your password and a final 2FA code to confirm.',
      'warning',
      'Disable'
    );
    if (confirmed) {
      setIsDisabling2FA(true);
    }
  };

  const confirmDisable2FA = async () => {
    try {
      await dispatch(disable2FA({ password: passwords.current, code: verificationCode })).unwrap();
      alertService.toast('success', 'Two-factor authentication disabled');
      setIsDisabling2FA(false);
      setVerificationCode('');
      setPasswords({ ...passwords, current: '' });
    } catch (err) {
      alertService.error('Error', err || 'MFA or Password verification failed.');
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = await alertService.confirm(
      'Delete Account?',
      'This will permanently delete your account, all connected devices, and message history. This action CANNOT be undone.',
      'danger',
      'Delete'
    );
    if (confirmed) {
      const password = await alertService.prompt('Delete Account', 'Enter your password to permanently delete your account', { input: 'password', confirmText: 'Delete Account' });
      if (password) {
        try {
          await dispatch(deleteAccount({ password })).unwrap();
          alertService.toast('success', 'Account deleted. Redirecting…');
        } catch (err) {
          alertService.error('Deletion Failed', err || 'Invalid password.');
        }
      }
    }
  };

  // Email Configuration Functions
  const loadEmailConfig = async () => {
    setEmailLoading(true);
    try {
      const response = await fetch('/api/email-config', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailConfig(data.config);
        setEmailFormData(prev => ({
          ...prev,
          ...data.config
        }));
      } else {
        alertService.error('Error', 'Failed to load email configuration');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to load email configuration');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    setEmailLoading(true);
    try {
      const response = await fetch('/api/email-config', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailFormData)
      });
      if (response.ok) {
        const data = await response.json();
        setEmailConfig(data.config);
        alertService.toast('success', 'Email configuration saved successfully');
      } else {
        alertService.error('Error', 'Failed to save email configuration');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to save email configuration');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestLoading(true);
    try {
      const response = await fetch('/api/email-config/test-smtp', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          smtpHost: emailFormData.smtpHost,
          smtpPort: emailFormData.smtpPort,
          smtpUser: emailFormData.smtpUser,
          smtpPassword: emailFormData.smtpPassword,
          smtpSecure: emailFormData.smtpSecure
        })
      });
      if (response.ok) {
        alertService.toast('success', 'SMTP connection verified successfully');
      } else {
        alertService.error('Error', 'SMTP verification failed');
      }
    } catch (err) {
      alertService.error('Error', 'SMTP verification failed');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      alertService.error('Error', 'Please enter a test email address');
      return;
    }

    setTestLoading(true);
    try {
      const response = await fetch('/api/email-config/send-test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testEmail })
      });
      if (response.ok) {
        alertService.toast('success', 'Test email sent successfully');
        setTestEmail('');
      } else {
        alertService.error('Error', 'Failed to send test email');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to send test email');
    } finally {
      setTestLoading(false);
    }
  };

  // SMS Configuration Functions
  const loadSmsConfig = async () => {
    setSmsLoading(true);
    try {
      const response = await fetch('/api/settings/sms-config', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsConfig(data.config);
        if (data.config) {
          setSmsFormData(prev => ({
            ...prev,
            smsEnabled: data.config.smsEnabled ?? false,
            provider: data.config.provider || 'msg91',
            apiKey: data.config.apiKeySet ? '••••••••••••••••' : '',
            accountSid: data.config.accountSid || '',
            fromNumber: data.config.fromNumber || '',
            senderId: data.config.senderId || '',
            route: data.config.route || '4',
          }));
        }
      } else {
        alertService.error('Error', 'Failed to load SMS configuration');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to load SMS configuration');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleSaveSmsConfig = async () => {
    setSmsLoading(true);
    try {
      const response = await fetch('/api/settings/sms-config', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(smsFormData)
      });
      if (response.ok) {
        const data = await response.json();
        setSmsConfig(data.config);
        alertService.toast('success', 'SMS configuration saved successfully');
      } else {
        alertService.error('Error', 'Failed to save SMS configuration');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to save SMS configuration');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleTestSms = async () => {
    if (!smsTestPhone) return alertService.error('Error', 'Enter a phone number to test');
    setSmsTestLoading(true);
    try {
      const response = await fetch('/api/settings/sms-config/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ to: smsTestPhone })
      });
      const data = await response.json();
      if (response.ok) {
        alertService.toast('success', 'Test SMS sent successfully');
        setSmsTestPhone('');
        loadSmsConfig();
      } else {
        alertService.error('SMS Failed', data.error || 'Could not send test SMS');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to send test SMS');
    } finally {
      setSmsTestLoading(false);
    }
  };

  const addEmail = () => {
    if (!newEmail) return;
    setEmailFormData(prev => ({
      ...prev,
      campaignCompletionEmails: [...(prev.campaignCompletionEmails || []), newEmail]
    }));
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setEmailFormData(prev => ({
      ...prev,
      campaignCompletionEmails: (prev.campaignCompletionEmails || []).filter(e => e !== email)
    }));
  };

  useEffect(() => {
    if (activeTab === 'email') {
      loadEmailConfig();
    }
    if (activeTab === 'sms') {
      loadSmsConfig();
    }
    if (activeTab === 'templates') {
      fetchTemplates();
    }
    if (activeTab === 'api') {
      fetchMcpKeys();
    }
  }, [activeTab]);

  const TABS = [
    { id: 'profile', label: 'Profile', icon: User, desc: 'Personal Branding' },
    { id: 'security', label: 'Security', icon: Lock, desc: 'Access Control' },
    { id: 'api', label: 'API Keys', icon: Code, desc: 'Developer Access' },
    { id: 'templates', label: 'Message Templates', icon: MessageSquare, desc: 'Email & SMS Content' },
    { id: 'email', label: 'Email Setup', icon: Mail, desc: 'SMTP Configuration' },
    { id: 'sms', label: 'SMS Gateway', icon: Smartphone, desc: 'SMS Provider Keys' },
    { id: 'usage', label: 'Usage', icon: Activity, desc: 'Resource Quotas' },
    { id: 'storage', label: 'Storage', icon: Cloud, desc: 'Cloud Infrastructure' },
    { id: 'account', label: 'Account', icon: Shield, desc: 'Lifecycle & MFA' }
  ];

  return (
    <DashboardModuleShell
      title="System Settings"
      subtitle="Configure your workspace identity, security, and developer integration parameters."
      icon={SettingsIcon}
    >
      <div className="flex flex-col lg:flex-row gap-8 items-start h-full pb-12">
        
        {/* Sidebar Navigation */}
        <Card className="w-full lg:w-[320px] p-2 shrink-0 flex lg:flex-col gap-1 overflow-x-auto">
          <div className="hidden lg:block px-4 py-3 mb-2 text-[11px] font-bold text-[#706E6B] uppercase border-b border-[#F3F2F2]">
            Configuration Matrix
          </div>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={cn(
 "shrink-0 whitespace-nowrap lg:w-full flex items-center gap-4 px-4 h-9 rounded transition-all duration-200 group relative",
 activeTab === t.id 
 ? "bg-[#F3F2F2] text-[#0176D3]" 
 : "text-[#5C5C5C] hover:bg-[#F7F9FB] hover:text-[#080707]"
 )}
            >
              <div className="shrink-0">
                <t.icon size={18} />
              </div>
              <div className="flex-1 text-left">
                <div className="text-[13px] font-bold leading-none">{t.label}</div>
                <div className="text-[10px] uppercase text-[#706E6B] mt-1">{t.desc}</div>
              </div>
              {activeTab === t.id && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-[#0176D3] rounded-l-full" />}
            </button>
          ))}
        </Card>

        {/* Main Content Area */}
        <div className="flex-1 w-full min-w-0">
          
          {/* PROFILE SECTION */}
          {activeTab === 'profile' && (
            <Card className="p-0 overflow-hidden animate-nebula-fade-in">
              <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                <h3 className="text-[16px] font-bold text-[#080707]">Profile Information</h3>
                <p className="text-[12px] text-[#706E6B] mt-1">Manage your professional identity and organization details.</p>
              </div>
                            <form onSubmit={handleProfileUpdate} className="p-8 space-y-8">
                {/* IDENTITY SECTION */}
                <div className="space-y-6">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Organization Identity</h4>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg border border-[#E5E5E5] bg-[#F7F9FB] flex items-center justify-center overflow-hidden shrink-0">
                      {user?.logoUrl ? (
                        <img src={user.logoUrl} alt="Company logo" className="w-full h-full object-contain" />
                      ) : (
                        <Building className="w-6 h-6 text-[#C9C9C9]" />
                      )}
                    </div>
                    <div>
                      <label className="text-[12px] px-3 py-1.5 rounded-lg bg-[#0176D3] text-white hover:bg-[#0176D3] cursor-pointer inline-block">
                        {logoUploading ? 'Uploading…' : (user?.logoUrl ? 'Change Logo' : 'Upload Logo')}
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
                      </label>
                      <p className="text-[11px] text-[#706E6B] mt-1.5">PNG or JPG. Printed on your customer invoice PDFs.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Company Name"
                      value={profileData.companyName} 
                      onChange={e => setProfileData({...profileData, companyName: e.target.value})} 
                    />
                    <Input 
                      label="Email Address (Locked)"
                      value={user?.email || ''} 
                      disabled 
                      className="bg-[#F7F9FB] opacity-60"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input 
                      label="Contact Number"
                      value={profileData.phoneNumber} 
                      onChange={e => setProfileData({...profileData, phoneNumber: e.target.value})} 
                    />
                    <Input 
                      label="Official Website"
                      placeholder="https://company.com"
                      value={profileData.website} 
                      onChange={e => setProfileData({...profileData, website: e.target.value})} 
                    />
                  </div>
                </div>

                {/* BUSINESS INFO SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Business Documentation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input 
                      label="GST Number"
                      placeholder="e.g. 22AAAAA0000A1Z5"
                      value={profileData.gstNumber} 
                      onChange={e => setProfileData({...profileData, gstNumber: e.target.value.toUpperCase()})} 
                    />
                    <div className="space-y-2 w-full">
                      <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Country / Region</label>
                      <CountrySelect
                        value={profileData.country}
                        onChange={country => setProfileData({ ...profileData, country, state: '' })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 w-full">
                      <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">State / Province</label>
                      <StateSelect
                        country={profileData.country}
                        value={profileData.state}
                        onChange={state => setProfileData({ ...profileData, state })}
                      />
                    </div>
                    <Input 
                      label="PIN / Postal Code"
                      value={profileData.pinCode} 
                      onChange={e => setProfileData({...profileData, pinCode: e.target.value})} 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Registered Business Address</label>
                    <textarea
                      className="w-full min-h-[100px] bg-white border border-[#D8DDE6] rounded p-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                      value={profileData.address}
                      onChange={e => setProfileData({...profileData, address: e.target.value})}
                      placeholder="Enter full street address, building, and unit number…"
                    />
                  </div>
                </div>

                {/* LEGAL & TAX IDENTIFIERS SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Legal & Tax Identifiers</h4>
                  <p className="text-[12px] text-[#706E6B] -mt-2">All optional — fill in whichever apply to your entity type. Printed on invoice PDFs where relevant.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input label="GST State Code" placeholder="e.g. 27" value={profileData.stateCode} onChange={e => setProfileData({ ...profileData, stateCode: e.target.value })} />
                    <Input label="PAN" value={profileData.pan} onChange={e => setProfileData({ ...profileData, pan: e.target.value.toUpperCase() })} />
                    <Input label="TAN" value={profileData.tan} onChange={e => setProfileData({ ...profileData, tan: e.target.value.toUpperCase() })} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input label="CIN" value={profileData.cin} onChange={e => setProfileData({ ...profileData, cin: e.target.value.toUpperCase() })} />
                    <Input label="MSME / Udyam Registration No." value={profileData.msmeNumber} onChange={e => setProfileData({ ...profileData, msmeNumber: e.target.value })} />
                    <Input label="IEC Number" value={profileData.iecNumber} onChange={e => setProfileData({ ...profileData, iecNumber: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input label="FSSAI Number" value={profileData.fssaiNumber} onChange={e => setProfileData({ ...profileData, fssaiNumber: e.target.value })} />
                    <Input label="UDYAM Number" value={profileData.udyamNumber} onChange={e => setProfileData({ ...profileData, udyamNumber: e.target.value })} />
                  </div>
                </div>

                {/* DIGITAL SIGNATURE & SEAL SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Digital Signature & Company Seal</h4>
                  <p className="text-[12px] text-[#706E6B] -mt-2">Printed near the totals block of every generated invoice PDF.</p>
                  <div className="flex flex-wrap gap-8">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-lg border border-[#E5E5E5] bg-[#F7F9FB] flex items-center justify-center overflow-hidden shrink-0">
                        {user?.digitalSignatureUrl ? (
                          <img src={user.digitalSignatureUrl} alt="Digital signature" className="w-full h-full object-contain" />
                        ) : (
                          <Pencil className="w-6 h-6 text-[#C9C9C9]" />
                        )}
                      </div>
                      <div>
                        <label className="text-[12px] px-3 py-1.5 rounded-lg bg-[#0176D3] text-white hover:bg-[#0176D3] cursor-pointer inline-block">
                          {signatureUploading ? 'Uploading…' : (user?.digitalSignatureUrl ? 'Change Signature' : 'Upload Signature')}
                          <input type="file" accept="image/*" className="hidden" onChange={handleSignatureUpload} disabled={signatureUploading} />
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-lg border border-[#E5E5E5] bg-[#F7F9FB] flex items-center justify-center overflow-hidden shrink-0">
                        {user?.companySealUrl ? (
                          <img src={user.companySealUrl} alt="Company seal" className="w-full h-full object-contain" />
                        ) : (
                          <ShieldCheck className="w-6 h-6 text-[#C9C9C9]" />
                        )}
                      </div>
                      <div>
                        <label className="text-[12px] px-3 py-1.5 rounded-lg bg-[#0176D3] text-white hover:bg-[#0176D3] cursor-pointer inline-block">
                          {sealUploading ? 'Uploading…' : (user?.companySealUrl ? 'Change Seal' : 'Upload Seal')}
                          <input type="file" accept="image/*" className="hidden" onChange={handleSealUpload} disabled={sealUploading} />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* INVOICE PAYMENT DETAILS SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Invoice Payment Details</h4>
                  <p className="text-[12px] text-[#706E6B] -mt-2">Shown as a "Pay to" box on generated Customer Invoice PDFs. Leave blank to omit.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Account Holder Name"
                      value={profileData.bank_accountName}
                      onChange={e => setProfileData({...profileData, bank_accountName: e.target.value})}
                    />
                    <Input
                      label="Account Number"
                      value={profileData.bank_accountNumber}
                      onChange={e => setProfileData({...profileData, bank_accountNumber: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input
                      label="Bank Name"
                      value={profileData.bank_bankName}
                      onChange={e => setProfileData({...profileData, bank_bankName: e.target.value})}
                    />
                    <Input
                      label="IFSC / SWIFT / Routing Code"
                      value={profileData.bank_ifscCode}
                      onChange={e => setProfileData({...profileData, bank_ifscCode: e.target.value.toUpperCase()})}
                    />
                    <Input
                      label="Branch"
                      value={profileData.bank_branch}
                      onChange={e => setProfileData({...profileData, bank_branch: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Invoice Footer Note</label>
                    <textarea
                      className="w-full min-h-[80px] bg-white border border-[#D8DDE6] rounded p-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                      value={profileData.invoiceFooterNote}
                      onChange={e => setProfileData({...profileData, invoiceFooterNote: e.target.value})}
                      placeholder="e.g. Payment due within 15 days. Late payments incur 2% interest."
                    />
                  </div>
                </div>

                {/* INVOICE NUMBERING SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Invoice Numbering</h4>
                  <p className="text-[12px] text-[#706E6B] -mt-2">Controls the format of every new Customer Invoice number — e.g. "CINV-202608-0001". Existing invoice numbers never change.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input
                      label="Prefix"
                      value={profileData.invoiceSettings.prefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, prefix: e.target.value } })}
                    />
                    <Input
                      label="Suffix"
                      value={profileData.invoiceSettings.suffix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, suffix: e.target.value } })}
                    />
                    <div className="space-y-2">
                      <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Period Format</label>
                      <select
                        className="w-full h-9 bg-white border border-[#D8DDE6] rounded px-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                        value={profileData.invoiceSettings.financialYearFormat}
                        onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, financialYearFormat: e.target.value } })}
                      >
                        <option value="calendar">Calendar month (YYYYMM)</option>
                        <option value="financial">Indian financial year (YY-YY)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Starting Number"
                      type="number"
                      min="1"
                      value={profileData.invoiceSettings.startingNumber}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, startingNumber: parseInt(e.target.value, 10) || 1 } })}
                    />
                    <Input
                      label="Number Padding (digits)"
                      type="number"
                      min="1"
                      max="10"
                      value={profileData.invoiceSettings.numberPadding}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, numberPadding: parseInt(e.target.value, 10) || 4 } })}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input
                      label="Credit Note Prefix"
                      value={profileData.invoiceSettings.creditNotePrefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, creditNotePrefix: e.target.value } })}
                    />
                    <Input
                      label="Debit Note Prefix"
                      value={profileData.invoiceSettings.debitNotePrefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, debitNotePrefix: e.target.value } })}
                    />
                    <Input
                      label="Proforma Prefix"
                      value={profileData.invoiceSettings.proformaPrefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, proformaPrefix: e.target.value } })}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Purchase Prefix"
                      value={profileData.invoiceSettings.purchasePrefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, purchasePrefix: e.target.value } })}
                    />
                    <Input
                      label="Delivery Challan Prefix"
                      value={profileData.invoiceSettings.deliveryChallanPrefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, deliveryChallanPrefix: e.target.value } })}
                    />
                    <Input
                      label="Sales Order Prefix"
                      value={profileData.invoiceSettings.salesOrderPrefix}
                      onChange={e => setProfileData({ ...profileData, invoiceSettings: { ...profileData.invoiceSettings, salesOrderPrefix: e.target.value } })}
                    />
                  </div>
                </div>

                {/* INVOICE WHATSAPP TEMPLATE SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Invoice WhatsApp Template</h4>
                  <p className="text-[12px] text-[#706E6B] -mt-2">
                    "Send via WhatsApp" on an invoice can only deliver a plain PDF if the customer messaged you in the last 24 hours.
                    Outside that window, WhatsApp requires an approved template message instead — pick which one to use here so sending never fails for that reason.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Template</label>
                      <select
                        className="w-full h-9 bg-white border border-[#D8DDE6] rounded px-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                        value={profileData.invoiceSettings.whatsappTemplate.name}
                        onChange={e => {
                          const tpl = waTemplates.find(t => t.name === e.target.value);
                          setProfileData({
                            ...profileData,
                            invoiceSettings: {
                              ...profileData.invoiceSettings,
                              whatsappTemplate: { name: e.target.value, language: tpl?.language || profileData.invoiceSettings.whatsappTemplate.language },
                            },
                          });
                        }}
                      >
                        <option value="">-- None (attachment-only; fails outside the 24h window) --</option>
                        {waTemplates.map(t => (
                          <option key={`${t.name}_${t.language}`} value={t.name}>{t.name} ({t.language})</option>
                        ))}
                      </select>
                      {waTemplates.length === 0 && (
                        <p className="text-[11px] text-[#706E6B]">No approved templates found — create and get one approved under WhatsApp Templates first.</p>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-[#706E6B]">
                    The chosen template's body is filled in with, in order: {'{{1}}'} = invoice number, {'{{2}}'} = amount due, {'{{3}}'} = customer name, {'{{4}}'} = due date — the number of {'{{n}}'} placeholders your approved template actually has decides how many of these get sent, so it works with a 1-, 2-, 3-, or 4-variable template as long as it's in this order.
                  </p>
                </div>

                {/* PAYMENT METHODS SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Payment Methods</h4>
                  <p className="text-[12px] text-[#706E6B] -mt-2">Offered when recording a payment against a Customer Invoice.</p>
                  <div className="space-y-2">
                    {paymentMethods.map(m => (
                      <div key={m.name} className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-[#E5E5E5] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                        <button type="button" onClick={() => togglePaymentMethodActive(m.name)} title={m.isActive ? 'Deactivate' : 'Activate'}>
                          {m.isActive ? <CheckCircle size={16} className="text-[#2E844A]" /> : <AlertCircle size={16} className="text-[#C9C9C9]" />}
                        </button>
                        <span className={cn('flex-1 text-[13px] font-medium', m.isActive ? 'text-[#444444]' : 'text-[#706E6B] line-through')}>{m.name}</span>
                        <button
                          type="button"
                          onClick={() => setDefaultPaymentMethod(m.name)}
                          className={cn('text-[13px] font-semibold px-2 py-1 rounded', m.isDefault ? 'bg-[#D8E6FE] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#5C5C5C]')}
                        >
                          {m.isDefault ? 'Default' : 'Set Default'}
                        </button>
                        <button type="button" onClick={() => removePaymentMethod(m.name)} className="text-[#706E6B] hover:text-[#BA0517]">
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Add a custom payment method (e.g. Crypto)"
                      value={newPaymentMethodName}
                      onChange={e => setNewPaymentMethodName(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addPaymentMethod}>Add</Button>
                  </div>
                </div>

                {/* INVOICE TERMS SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Invoice Terms</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Payment Due (days)"
                      type="number"
                      min="0"
                      value={profileData.invoiceTerms.paymentDueDays}
                      onChange={e => setProfileData({ ...profileData, invoiceTerms: { ...profileData.invoiceTerms, paymentDueDays: parseInt(e.target.value, 10) || 0 } })}
                    />
                    <Input
                      label="Jurisdiction"
                      placeholder="e.g. Courts of Mumbai, India"
                      value={profileData.invoiceTerms.jurisdiction}
                      onChange={e => setProfileData({ ...profileData, invoiceTerms: { ...profileData.invoiceTerms, jurisdiction: e.target.value } })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Late Payment Note</label>
                    <textarea
                      className="w-full min-h-[60px] bg-white border border-[#D8DDE6] rounded p-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                      value={profileData.invoiceTerms.latePaymentNote}
                      onChange={e => setProfileData({ ...profileData, invoiceTerms: { ...profileData.invoiceTerms, latePaymentNote: e.target.value } })}
                      placeholder="e.g. 2% interest per month on overdue amounts."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Warranty Terms</label>
                    <textarea
                      className="w-full min-h-[60px] bg-white border border-[#D8DDE6] rounded p-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                      value={profileData.invoiceTerms.warrantyTerms}
                      onChange={e => setProfileData({ ...profileData, invoiceTerms: { ...profileData.invoiceTerms, warrantyTerms: e.target.value } })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Return Policy</label>
                    <textarea
                      className="w-full min-h-[60px] bg-white border border-[#D8DDE6] rounded p-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                      value={profileData.invoiceTerms.returnPolicy}
                      onChange={e => setProfileData({ ...profileData, invoiceTerms: { ...profileData.invoiceTerms, returnPolicy: e.target.value } })}
                    />
                  </div>
                </div>

                {/* REGIONAL SETTINGS SECTION */}
                <div className="space-y-6 pt-4">
                  <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Regional Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Timezone</label>
                      <select
                        className="w-full h-9 bg-white border border-[#D8DDE6] rounded px-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                        value={profileData.timezone}
                        onChange={e => setProfileData({ ...profileData, timezone: e.target.value })}
                      >
                        {/* The current value may not appear in Intl.supportedValuesOf('timeZone')
                            on every runtime (e.g. 'Asia/Kolkata' vs the older 'Asia/Calcutta' alias
                            some ICU builds enumerate instead) even though it's a valid timeZone —
                            always include it explicitly so the dropdown never silently mismatches. */}
                        {(getSupportedTimezones().includes(profileData.timezone)
                          ? getSupportedTimezones()
                          : [profileData.timezone, ...getSupportedTimezones()]
                        ).map(tz => <option key={tz} value={tz}>{tz}</option>)}
                      </select>
                      <p className="text-[11px] text-[#706E6B]">Used for "today"/day boundaries across Calendar, Tasks, and reports.</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Base Currency</label>
                      <select
                        className="w-full h-9 bg-white border border-[#D8DDE6] rounded px-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444]"
                        value={profileData.baseCurrency}
                        onChange={e => setProfileData({ ...profileData, baseCurrency: e.target.value })}
                      >
                        {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <p className="text-[11px] text-[#706E6B]">Cross-currency totals (dashboard, forecasts) are converted into this currency.</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-[#F3F2F2] flex justify-end">
                  <Button type="submit" variant="brand" className="px-10 h-11 text-sm font-bold shadow-sm shadow-blue-500/20" loading={loading}>
                    Save
                  </Button>
                </div>
              </form>

              {/* EXCHANGE RATES */}
              <div className="p-8 pt-0 space-y-4">
                <h4 className="text-[11px] font-bold text-[#706E6B] border-b border-[#F3F2F2] pb-2">Exchange Rates</h4>
                <p className="text-[12px] text-[#706E6B]">
                  1 unit of each currency below equals this many units of your base currency ({profileData.baseCurrency}). Update these periodically — rates are not fetched automatically.
                </p>
                <div className="space-y-3">
                  {SUPPORTED_CURRENCIES.filter(c => c !== profileData.baseCurrency).map(currency => (
                    <div key={currency} className="flex items-center gap-3">
                      <span className="w-16 text-[13px] font-bold text-[#5C5C5C]">{currency}</span>
                      <span className="text-[12px] text-[#706E6B]">=</span>
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        className="w-32 h-9 bg-white border border-[#D8DDE6] rounded px-3 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                        value={rates[currency] ?? ''}
                        onChange={e => handleRateChange(currency, e.target.value)}
                        placeholder="e.g. 83"
                      />
                      <span className="text-[12px] text-[#706E6B]">{profileData.baseCurrency}</span>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-9 px-4 text-[12px]"
                        loading={ratesLoading}
                        onClick={() => handleRateSave(currency)}
                      >
                        Save
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* SECURITY SECTION */}
          {activeTab === 'security' && (
            <Card className="p-0 overflow-hidden animate-nebula-fade-in">
              <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                <h3 className="text-[16px] font-bold text-[#080707]">Password</h3>
                <p className="text-[12px] text-[#706E6B] mt-1">Update your password to ensure your account remains protected.</p>
              </div>
              
              <form onSubmit={handlePasswordUpdate} className="p-8 space-y-6">
                <div className="max-w-md">
                  <Input 
                    label="Current Password"
                    type="password" 
                    value={passwords.current} 
                    onChange={e => setPasswords({...passwords, current: e.target.value})} 
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  <Input 
                    label="New Password"
                    type="password" 
                    value={passwords.next} 
                    onChange={e => setPasswords({...passwords, next: e.target.value})} 
                  />
                  <Input 
                    label="Confirm New Password"
                    type="password" 
                    value={passwords.confirm} 
                    onChange={e => setPasswords({...passwords, confirm: e.target.value})} 
                  />
                </div>

                <div className="p-4 rounded-[4px] bg-[#FEF1E5] border border-[#FDE4C9] flex items-start gap-3">
                  <AlertCircle size={16} className="text-[#DD7A01] shrink-0 mt-0.5" />
                  <p className="text-[12px] text-[#DD7A01] leading-relaxed">
                    Modifying your password will invalidate all existing sessions across other devices for your security.
                  </p>
                </div>

                <div className="pt-6 border-t border-[#F3F2F2] flex justify-end">
                  <Button 
                    variant="brand" 
                    icon={ShieldCheck} 
                    loading={loading} 
                    type="submit"
                  >
                    Update Password
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* API SECTION */}
          {activeTab === 'api' && (
            <div className="space-y-6">
            <Card className="p-0 overflow-hidden animate-nebula-fade-in">
              <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                <h3 className="text-[16px] font-bold text-[#080707]">Developer Settings</h3>
                <p className="text-[12px] text-[#706E6B] mt-1">Configure API keys for third-party integrations and custom automations.</p>
              </div>

              <div className="p-8 space-y-8">
                <div className="p-6 bg-[#F7F9FB] border border-[#F3F2F2] rounded-lg space-y-4 shadow-inner">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-[#706E6B] leading-none">Primary Master Key Signature</label>
                    <Badge variant={user?.apiKey ? "emerald" : "amber"}>
                       {user?.apiKey ? "Operational" : "Link Required"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 bg-white border border-[#E5E5E5] p-4 rounded-lg font-mono text-[13px] text-[#444444] flex items-center justify-between min-h-[56px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                      <span className="truncate max-w-[400px]">
                        {user?.apiKey
                          ? (apiKeyShow ? user?.apiKey : '••••••••••••••••••••••••••••••••••••••••')
                          : <span className="text-[#C9C9C9] italic">No integration key generated for this account.</span>
                        }
                      </span>
                      {user?.apiKey && (
                        <button
                          onClick={() => setApiKeyShow(!apiKeyShow)}
                          className="text-[#706E6B] hover:text-[#0176D3] transition-colors p-1"
                          title={apiKeyShow ? "Hide Key" : "Show Key"}
                        >
                          {apiKeyShow ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      )}
                    </div>
                    {user?.apiKey && (
                       <Button
                        variant="outline"
                        size="sm"
                        icon={Copy}
                        className="h-14 px-6 border-[#E5E5E5] hover:border-[#0176D3]/20"
                        onClick={() => {
                          navigator.clipboard.writeText(user?.apiKey);
                          alertService.toast('success', 'Security fragment cached to clipboard');
                        }}
                       >
                         Copy Signature
                       </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-[#706E6B] font-bold uppercase pl-1 leading-relaxed">
                    This global signature grants high-latency administrative uplink capability. Preserve with absolute priority.
                  </p>
                </div>

                <div>
                  <Button variant="secondary" icon={RefreshCw} loading={loading} onClick={handleGenerateKey}>
                    Regenerate Master Key
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-0 overflow-hidden animate-nebula-fade-in">
              <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                <h3 className="text-[16px] font-bold text-[#080707]">Claude / MCP Integration</h3>
                <p className="text-[12px] text-[#706E6B] mt-1">Connect this CRM to Claude (or any MCP-compatible client) so it can read and manage your leads, deals, contacts, calendar and tasks directly.</p>
              </div>

              {!hasAgenticMode ? (
                <div className="p-8">
                  <div className="flex items-start gap-4 p-5 bg-[#FEF1E5] border border-[#FDE4C9] rounded-lg">
                    <Lock size={20} className="text-[#DD7A01] shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-[13px] font-bold text-[#A96404]">
                        Agentic Mode is not included in your {agenticPlan?.name ?? 'current'} plan
                      </p>
                      <p className="text-[12px] text-[#DD7A01] leading-relaxed">
                        Upgrade to let Claude (or any MCP-compatible AI assistant) connect directly to this CRM and act on your leads, deals, contacts, and tasks.
                      </p>
                      <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/subscription')}>
                        Upgrade Plan
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="p-8 space-y-6">
                {newMcpKey && (
                  <div className="p-4 bg-[#FEF1E5] border border-[#FDE4C9] rounded-lg space-y-2">
                    <p className="text-[11px] font-bold text-[#DD7A01] uppercase ">Copy this key now — it will not be shown again</p>
                    <div className="flex items-center gap-3">
                      <code className="flex-1 bg-white border border-[#FDE4C9] p-3 rounded-lg font-mono text-[12px] text-[#2E2E2E] break-all">{newMcpKey}</code>
                      <Button
                        variant="outline" size="sm" icon={Copy}
                        onClick={() => { navigator.clipboard.writeText(newMcpKey); alertService.toast('success', 'Key copied'); }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Input
                      label="New key name"
                      placeholder="e.g. Claude Desktop"
                      value={mcpKeyName}
                      onChange={e => setMcpKeyName(e.target.value)}
                    />
                  </div>
                  <Button icon={Key} loading={mcpKeyCreating} onClick={handleCreateMcpKey}>
                    Generate Key
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-[#706E6B]">Key type</label>
                    <select value={mcpKeyKind} onChange={e => setMcpKeyKind(e.target.value)} className="mt-1 w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px]">
                      <option value="mcp">Claude / MCP (read and write your CRM)</option>
                      <option value="agent">Agent API (let your own chatbot talk to your support agent)</option>
                    </select>
                  </div>
                  <Input
                    label="Only allow these IP addresses (optional, comma separated)"
                    placeholder="203.0.113.9, 198.51.100.4"
                    value={mcpKeyIps}
                    onChange={e => setMcpKeyIps(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] ">Active Keys</label>
                  {mcpKeysLoading ? (
                    <p className="text-[12px] text-[#706E6B]">Loading…</p>
                  ) : mcpKeys.length === 0 ? (
                    <p className="text-[12px] text-[#706E6B] italic">No keys yet — generate one above to connect Claude.</p>
                  ) : (
                    <div className="border border-[#F3F2F2] rounded-lg divide-y divide-[#F3F2F2]">
                      {mcpKeys.map(k => (
                        <div key={k._id} className="flex items-center justify-between px-4 py-3">
                          <div>
                            <div className="text-[13px] font-bold text-[#2E2E2E]">{k.name}</div>
                            <div className="text-[11px] text-[#706E6B] font-mono">{k.keyPrefix}•••••••• {(k.scopes || []).includes('agent') ? '· Agent API' : ''}{(k.allowedIps || []).length ? ` · IPs: ${k.allowedIps.join(', ')}` : ''}</div>
                          </div>
                          <button
                            onClick={() => handleRevokeMcpKey(k._id)}
                            className="text-[11px] font-bold text-[#BA0517] hover:text-[#BA0517]"
                          >
                            Revoke
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-6 bg-[#F7F9FB] border border-[#F3F2F2] rounded-lg space-y-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] ">Setup — Claude Code / Desktop</label>
                  <p className="text-[12px] text-[#706E6B]">Run this locally, using a key generated above:</p>
                  <code className="block bg-white border border-[#E5E5E5] p-3 rounded-lg font-mono text-[12px] text-[#444444] overflow-x-auto whitespace-pre">
{`claude mcp add --transport http crm ${window.location.origin}/api/mcp \\
  --header "Authorization: Bearer YOUR_KEY_HERE"`}
                  </code>
                </div>
              </div>
              )}
            </Card>
            </div>
          )}

          {/* EMAIL SECTION */}
          {activeTab === 'email' && (
            <div className="space-y-6 animate-nebula-fade-in">
              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <h3 className="text-[16px] font-bold text-[#080707]">SMTP Configuration</h3>
                  <p className="text-[12px] text-[#706E6B] mt-1">Configure email settings for campaign notifications and transactional emails.</p>
                </div>

                <div className="p-8 space-y-6">
                  <Toggle
                    checked={emailFormData.smtpEnabled}
                    onChange={checked => setEmailFormData({ ...emailFormData, smtpEnabled: checked })}
                    label="Enable Email Notifications"
                  />

                  {emailFormData.smtpEnabled && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="SMTP Host"
                          value={emailFormData.smtpHost}
                          onChange={e => setEmailFormData({ ...emailFormData, smtpHost: e.target.value })}
                          placeholder="smtp.gmail.com"
                        />
                        <Input
                          label="SMTP Port"
                          type="number"
                          value={emailFormData.smtpPort}
                          onChange={e => setEmailFormData({ ...emailFormData, smtpPort: parseInt(e.target.value) })}
                          placeholder="587"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="SMTP User"
                          value={emailFormData.smtpUser}
                          onChange={e => setEmailFormData({ ...emailFormData, smtpUser: e.target.value })}
                          placeholder="your-email@gmail.com"
                        />
                        <Input
                          label="SMTP Password"
                          type="password"
                          value={emailFormData.smtpPassword}
                          onChange={e => setEmailFormData({ ...emailFormData, smtpPassword: e.target.value })}
                          placeholder="App password or password"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="From Email"
                          value={emailFormData.smtpFromEmail}
                          onChange={e => setEmailFormData({ ...emailFormData, smtpFromEmail: e.target.value })}
                          placeholder="noreply@yourcompany.com"
                        />
                        <Input
                          label="From Name"
                          value={emailFormData.smtpFromName}
                          onChange={e => setEmailFormData({ ...emailFormData, smtpFromName: e.target.value })}
                          placeholder="WaapLeads"
                        />
                      </div>

                      <div className="p-4 bg-[#F7F9FB] rounded-[4px] border border-[#E5E5E5]">
                        <Toggle
                          checked={emailFormData.smtpSecure}
                          onChange={checked => setEmailFormData({ ...emailFormData, smtpSecure: checked })}
                          label="Use TLS/SSL Encryption"
                        />
                      </div>

                      <Button
                        variant="outline"
                        loading={testLoading}
                        onClick={handleTestSmtp}
                        className="w-full"
                      >
                        Test SMTP Connection
                      </Button>
                    </>
                  )}
                </div>
              </Card>

              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <h3 className="text-[16px] font-bold text-[#080707]">Notification Preferences</h3>
                  <p className="text-[12px] text-[#706E6B] mt-1">Configure how and when you receive email notifications.</p>
                </div>

                <div className="p-8 space-y-6">
                  <Toggle
                    checked={emailFormData.notificationsEnabled}
                    onChange={checked => setEmailFormData({ ...emailFormData, notificationsEnabled: checked })}
                    label="Enable Notifications"
                  />

                  <div className="space-y-2">
                    <label className="text-[11px] font-semibold text-[#706E6B] ">Notification Mode</label>
                    <select
                      value={emailFormData.notificationMode}
                      onChange={e => setEmailFormData({ ...emailFormData, notificationMode: e.target.value })}
                      className="w-full px-4 h-9 border border-[#D8DDE6] rounded-[4px] text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                    >
                      <option value="instant">Instant - Send immediately on completion</option>
                      <option value="daily_digest">Daily Digest - Once per day</option>
                      <option value="both">Both - Instant + Daily digest</option>
                    </select>
                  </div>

                  {(emailFormData.notificationMode === 'daily_digest' || emailFormData.notificationMode === 'both') && (
                    <Input
                      label="Daily Digest Time"
                      type="time"
                      value={emailFormData.dailyDigestTime}
                      onChange={e => setEmailFormData({ ...emailFormData, dailyDigestTime: e.target.value })}
                    />
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-3 bg-[#F7F9FB] rounded-[4px] border border-[#E5E5E5] cursor-pointer hover:border-[#0176D3] transition-all">
                      <input
                        type="checkbox"
                        checked={emailFormData.attachReportPDF}
                        onChange={e => setEmailFormData({ ...emailFormData, attachReportPDF: e.target.checked })}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-[12px] font-bold text-[#5C5C5C] uppercase ">Attach PDF Report</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-[#F7F9FB] rounded-[4px] border border-[#E5E5E5] cursor-pointer hover:border-[#0176D3] transition-all">
                      <input
                        type="checkbox"
                        checked={emailFormData.attachDetailedCSV}
                        onChange={e => setEmailFormData({ ...emailFormData, attachDetailedCSV: e.target.checked })}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-[12px] font-bold text-[#5C5C5C] uppercase ">Attach CSV Data</span>
                    </label>
                  </div>
                </div>
              </Card>

              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <h3 className="text-[16px] font-bold text-[#080707]">Campaign Recipients</h3>
                  <p className="text-[12px] text-[#706E6B] mt-1">Add email addresses that receive campaign completion notifications.</p>
                </div>

                <div className="p-8 space-y-6">
                  <div className="flex gap-3">
                    <Input
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && addEmail()}
                      placeholder="email@example.com"
                    />
                    <Button
                      variant="secondary"
                      className="shrink-0"
                      onClick={addEmail}
                    >
                      Add Email
                    </Button>
                  </div>

                  {emailFormData.campaignCompletionEmails && emailFormData.campaignCompletionEmails.length > 0 && (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {emailFormData.campaignCompletionEmails.map(email => (
                        <div key={email} className="flex items-center justify-between p-3 bg-[#F7F9FB] rounded-[4px] border border-[#E5E5E5]">
                          <span className="text-[13px] text-[#444444]">{email}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={X}
                            onClick={() => removeEmail(email)}
                            className="text-[#BA0517] hover:text-[#BA0517]"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <h3 className="text-[16px] font-bold text-[#080707]">Test Email</h3>
                  <p className="text-[12px] text-[#706E6B] mt-1">Send a test email to verify your configuration is working correctly.</p>
                </div>

                <div className="p-8 space-y-4">
                  <div className="flex gap-3">
                    <Input
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                    />
                    <Button
                      variant="brand"
                      className="shrink-0"
                      loading={testLoading}
                      onClick={handleSendTestEmail}
                    >
                      Send Test
                    </Button>
                  </div>

                  <div className="p-4 rounded-[4px] bg-[#EEF4FF] border border-[#D8E6FE] flex items-start gap-3">
                    <AlertCircle size={16} className="text-[#0176D3] shrink-0 mt-0.5" />
                    <p className="text-[12px] text-[#0176D3] leading-relaxed">
                      💡 Use this to verify your SMTP configuration is working correctly before enabling campaign notifications.
                    </p>
                  </div>
                </div>
              </Card>

              <div className="pt-2 flex justify-end">
                <Button
                  variant="brand"
                  size="lg"
                  loading={emailLoading}
                  onClick={handleSaveEmailConfig}
                  className="px-12 shadow-sm shadow-blue-500/20"
                >
                  Save Email Configuration
                </Button>
              </div>
            </div>
          )}

          {/* SMS GATEWAY SECTION */}
          {activeTab === 'sms' && (
            <div className="space-y-6 animate-nebula-fade-in">
              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#EEF4FF] rounded-lg">
                      <Smartphone size={20} className="text-[#0176D3]" />
                    </div>
                    <div>
                      <h3 className="text-[16px] font-bold text-[#080707]">SMS Gateway Configuration</h3>
                      <p className="text-[12px] text-[#706E6B] mt-0.5">Connect your own SMS provider. Credentials are AES-256 encrypted and isolated per organisation.</p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-8">
                  {/* Enable toggle */}
                  <Toggle
                    checked={smsFormData.smsEnabled}
                    onChange={checked => setSmsFormData(p => ({ ...p, smsEnabled: checked }))}
                    label="Enable SMS Notifications"
                  />

                  {/* Provider selector */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-semibold text-[#706E6B] ml-1">SMS Provider</label>
                    <select
                      value={smsFormData.provider}
                      onChange={e => setSmsFormData(p => ({ ...p, provider: e.target.value, apiKey: '' }))}
                      className="w-full px-4 h-9 border border-[#D8DDE6] rounded-[4px] text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                    >
                      <option value="msg91">MSG91 (India)</option>
                      <option value="textlocal">TextLocal (India)</option>
                      <option value="fast2sms">Fast2SMS (India)</option>
                      <option value="2factor">2Factor (India)</option>
                      <option value="twilio">Twilio (International)</option>
                    </select>
                  </div>

                  {/* Provider-specific fields */}
                  {smsFormData.provider === 'twilio' ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="Account SID"
                          value={smsFormData.accountSid}
                          onChange={e => setSmsFormData(p => ({ ...p, accountSid: e.target.value }))}
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        />
                        <Input
                          label="Auth Token"
                          type="password"
                          value={smsFormData.apiKey}
                          onChange={e => setSmsFormData(p => ({ ...p, apiKey: e.target.value }))}
                          placeholder={smsConfig?.apiKeySet ? '••••••••••••••••' : 'Your Twilio auth token'}
                        />
                      </div>
                      <Input
                        label="Twilio From Number"
                        value={smsFormData.fromNumber}
                        onChange={e => setSmsFormData(p => ({ ...p, fromNumber: e.target.value }))}
                        placeholder="+1415xxxxxxx"
                        hint="Your Twilio phone number in E.164 format"
                      />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <Input
                        label="API Key"
                        type="password"
                        value={smsFormData.apiKey}
                        onChange={e => setSmsFormData(p => ({ ...p, apiKey: e.target.value }))}
                        placeholder={smsConfig?.apiKeySet ? '••••••••••••••••' : `Your ${smsFormData.provider.toUpperCase()} API key`}
                        hint="Stored encrypted — paste new value only to rotate"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="Sender ID / DLT Header"
                          value={smsFormData.senderId}
                          onChange={e => setSmsFormData(p => ({ ...p, senderId: e.target.value }))}
                          placeholder="ADPLT"
                          hint="DLT registered 6-char sender ID"
                        />
                        {smsFormData.provider === 'msg91' && (
                          <div className="space-y-2">
                            <label className="text-[11px] font-semibold text-[#706E6B] ml-1">Route</label>
                            <select
                              value={smsFormData.route}
                              onChange={e => setSmsFormData(p => ({ ...p, route: e.target.value }))}
                              className="w-full px-4 h-9 border border-[#D8DDE6] rounded-[4px] text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                            >
                              <option value="4">Route 4 — Transactional</option>
                              <option value="1">Route 1 — Promotional</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Status badge */}
                  {smsConfig && (
                    <div className="flex items-center gap-3 p-4 bg-[#F7F9FB] rounded-[4px] border border-[#E5E5E5]">
                      <div className={cn(
 "w-2.5 h-2.5 rounded-full shrink-0",
 smsConfig.smsVerified ? "bg-[#2E844A]" : "bg-[#D8DDE6]"
 )} />
                      <span className="text-[12px] font-bold text-[#5C5C5C] uppercase ">
                        {smsConfig.smsVerified
                          ? `Verified · Last tested ${smsConfig.lastTestAt ? new Date(smsConfig.lastTestAt).toLocaleDateString() : 'N/A'}`
                          : 'Not yet verified — send a test SMS below'}
                      </span>
                    </div>
                  )}

                  <div className="pt-6 border-t border-[#F3F2F2] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[11px] font-bold text-[#706E6B] uppercase ">
                      <ShieldCheck size={14} className="text-[#2E844A]" /> AES-256 Credential Encryption
                    </div>
                    <Button
                      variant="brand"
                      icon={Save}
                      loading={smsLoading}
                      onClick={handleSaveSmsConfig}
                      className="px-8 shadow-sm shadow-blue-500/20"
                    >
                      Save SMS Config
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Test SMS */}
              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <h3 className="text-[16px] font-bold text-[#080707]">Send Test SMS</h3>
                  <p className="text-[12px] text-[#706E6B] mt-1">Verify your gateway is working by sending a live test message.</p>
                </div>
                <div className="p-8 space-y-4">
                  <div className="flex gap-3">
                    <Input
                      value={smsTestPhone}
                      onChange={e => setSmsTestPhone(e.target.value)}
                      placeholder="+919876543210"
                      hint="Include country code"
                    />
                    <Button
                      variant="brand"
                      className="shrink-0"
                      loading={smsTestLoading}
                      onClick={handleTestSms}
                    >
                      Send Test
                    </Button>
                  </div>
                  <div className="p-4 rounded-[4px] bg-[#EEF4FF] border border-[#D8E6FE] flex items-start gap-3">
                    <Info size={16} className="text-[#0176D3] shrink-0 mt-0.5" />
                    <p className="text-[12px] text-[#0176D3] leading-relaxed">
                      Save your configuration first, then use this to verify credentials are working before enabling live SMS notifications.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* TEMPLATES SECTION */}
          {activeTab === 'templates' && (
            <div className="space-y-6 animate-nebula-fade-in">
              <Card className="p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#F3F2F2]">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50 flex justify-between items-center">
                  <div>
                    <h3 className="text-[16px] font-bold text-[#080707]">Message Templates</h3>
                    <p className="text-[12px] text-[#706E6B] mt-1">Manage global system templates or create your own overrides.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant={activeTemplateType === 'email' ? 'brand' : 'outline'}
                      onClick={() => { setActiveTemplateType('email'); setEditingTemplate(null); }}
                    >
                      Email
                    </Button>
                    <Button 
                      variant={activeTemplateType === 'sms' ? 'brand' : 'outline'}
                      onClick={() => { setActiveTemplateType('sms'); setEditingTemplate(null); }}
                    >
                      SMS
                    </Button>
                  </div>
                </div>

                <div className="p-8">
                  {editingTemplate ? (
                    <form onSubmit={handleSaveTemplate} className="space-y-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-[#080707]">
                          {editingTemplate._id ? 'Edit Template' : 'New Override'}
                        </h4>
                        <Button variant="ghost" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                      </div>
                      
                      <Input
                        label="Template Key (System ID)"
                        value={editingTemplate.templateKey}
                        disabled={!!editingTemplate._id}
                        onChange={e => setEditingTemplate({...editingTemplate, templateKey: e.target.value})}
                      />

                      {activeTemplateType === 'email' && (
                        <Input
                          label="Email Subject"
                          value={editingTemplate.subject || ''}
                          onChange={e => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                        />
                      )}

                      <div className="space-y-2">
                        <label className="text-[#5C5C5C] text-[12px] font-medium ml-0.5">Template Content</label>
                        <textarea
                          className="w-full min-h-[200px] bg-white border border-[#D8DDE6] rounded p-4 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all text-[#444444] font-mono"
                          value={activeTemplateType === 'email' ? (editingTemplate.html || '') : (editingTemplate.body || '')}
                          onChange={e => setEditingTemplate({
                            ...editingTemplate, 
                            [activeTemplateType === 'email' ? 'html' : 'body']: e.target.value
                          })}
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button variant="brand" type="submit" loading={templatesLoading}>Save Template</Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      {templatesLoading ? (
                        <div className="py-12 flex justify-center"><Loader className="animate-spin text-[#706E6B]" /></div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {(activeTemplateType === 'email' ? emailTemplates : smsTemplates).map(t => (
                            <div key={t._id} className="p-4 border border-[#E5E5E5] rounded-lg hover:border-[#0176D3] transition-colors bg-white relative shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                              <div className="flex justify-between items-start mb-2">
                                <h5 className="font-bold text-[14px] text-[#2E2E2E]">{t.templateKey}</h5>
                                {t.companyId && <Badge variant="blue">Override</Badge>}
                                {!t.companyId && <Badge variant="slate">System Default</Badge>}
                              </div>
                              <p className="text-[12px] text-[#706E6B] line-clamp-2">
                                {activeTemplateType === 'email' ? t.subject : t.body}
                              </p>
                              <div className="flex gap-2 mt-4 pt-4 border-t border-[#F3F2F2]">
                                <Button size="sm" variant="outline" className="flex-1 text-[12px]" onClick={() => setEditingTemplate(t)}>
                                  <Pencil size={12} className="mr-1" /> Edit Override
                                </Button>
                                {t.companyId && (
                                  <Button size="sm" variant="danger" outline onClick={() => handleDeleteTemplate(t.templateKey)}>
                                    <Trash size={12} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {/* Allow creating an override from scratch if they know the key */}
                          <div className="p-4 border border-dashed border-[#D8DDE6] rounded-lg flex flex-col items-center justify-center text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3] transition-colors cursor-pointer bg-[#F7F9FB]/50 min-h-[150px]"
                               onClick={() => setEditingTemplate({ templateKey: '' })}>
                            <Pencil size={24} className="mb-2" />
                            <span className="text-[13px] font-bold">Create New Override</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* USAGE SECTION */}
          {activeTab === 'usage' && (
            <Card className="p-0 overflow-hidden animate-nebula-fade-in shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#F3F2F2]">
              <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                <h3 className="text-[16px] font-bold text-[#080707]">Resource monitor</h3>
                <p className="text-[12px] text-[#706E6B] mt-1">Visualize your current plan consumption and matrix quotas.</p>
              </div>
              
              <div className="p-8 space-y-10">
                <div className="space-y-6">
                  <div className="flex justify-between items-end">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <MessageSquare size={16} className="text-[#0176D3]" />
                        <span className="text-[13px] font-bold text-[#444444] uppercase ">Messaging Bandwidth</span>
                      </div>
                      <div className="text-[28px] font-bold text-[#080707] tabular-nums">
                        {usage ? usage.messages.toLocaleString() : '—'}
                        <span className="text-sm text-[#706E6B] font-medium"> / {usage ? usage.monthlyMessageLimit.toLocaleString() : '—'} Messages</span>
                      </div>
                    </div>
                    <Badge variant="blue">{usage ? `${usage.messagesPercentageUsed}% Used` : '…'}</Badge>
                  </div>
                  <div className="h-2.5 w-full bg-[#F3F2F2] rounded-full overflow-hidden">
                    <div className="h-full bg-[#0176D3] rounded-full transition-all duration-1000" style={{ width: `${Math.min(usage?.messagesPercentageUsed || 0, 100)}%` }} />
                  </div>
                  <div className="flex justify-between text-[11px] font-bold text-[#706E6B] uppercase ">
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      {usage?.subscriptionEndDate
                        ? `Renews ${new Date(usage.subscriptionEndDate).toLocaleDateString()}`
                        : 'No active subscription'}
                    </div>
                    <div>Tier: {usage?.planName || '—'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {[
                    { label: 'Devices Connected', val: usage ? `${usage.deviceCount} / ${usage.maxDevices ?? '∞'}` : '—', icon: Smartphone, color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
                    { label: 'Team Seats', val: usage ? `${usage.seatCount} / ${usage.maxUsers ?? '∞'}` : '—', icon: Zap, color: 'text-[#DD7A01]', bg: 'bg-[#FDE4C9]' },
                    { label: 'AI Tokens This Month', val: usage ? usage.aiTokens.toLocaleString() : '—', icon: Activity, color: 'text-[#2E844A]', bg: 'bg-[#CDEFC4]' }
                  ].map((m, i) => (
                    <div key={i} className="p-6 border border-[#E5E5E5] rounded-[4px] flex flex-col items-center gap-4 hover:bg-[#F7F9FB]/50 transition-all">
                      <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", m.bg, m.color)}>
                        <m.icon size={22} />
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">{m.label}</div>
                        <div className="text-[18px] font-bold text-[#080707] font-mono">{m.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* STORAGE SECTION */}
          {activeTab === 'storage' && (
            <Card className="p-0 overflow-hidden animate-nebula-fade-in shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#F3F2F2]">
              <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#EEF4FF] rounded-lg">
                    <Cloud size={20} className="text-[#0176D3]" />
                  </div>
                  <div>
                    <h3 className="text-[16px] font-bold text-[#080707]">Cloud Media Infrastructure</h3>
                    <p className="text-[12px] text-[#706E6B] mt-0.5">Configure your independent ImageKit.io environment for media hosting.</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8 space-y-10">
                <div className="flex items-start gap-4 p-5 bg-[#EEF4FF]/50 border border-[#D8E6FE] rounded-[4px]">
                  <Info size={18} className="text-[#0176D3] shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold text-[#014486] uppercase ">Independent Architecture Identity</p>
                    <p className="text-[12px] text-[#0176D3] leading-relaxed">
                      By configuring your own ImageKit keys, your business retains full ownership of all uploaded media assets. If left blank, the system will utilize global cloud infrastructure.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[11px] font-semibold text-[#706E6B] ml-1">CDN Endpoint Signature</label>
                    <Input 
                      icon={Globe}
                      value={profileData.imagekitUrlEndpoint} 
                      onChange={e => setProfileData({...profileData, imagekitUrlEndpoint: e.target.value})} 
                      placeholder="https://ik.imagekit.io/unique_id"
                      hint="Found in your ImageKit Dashboard → Settings → URL Endpoints"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[11px] font-semibold text-[#706E6B] ml-1">Public Key Identifier</label>
                      <Input 
                        icon={Key}
                        value={profileData.imagekitPublicKey} 
                        onChange={e => setProfileData({...profileData, imagekitPublicKey: e.target.value})} 
                        placeholder="public_…"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-semibold text-[#706E6B] ml-1">Private Security Key</label>
                      <Input 
                        icon={Shield}
                        type="password" 
                        value={profileData.imagekitPrivateKey} 
                        onChange={e => setProfileData({...profileData, imagekitPrivateKey: e.target.value})} 
                        placeholder={user?.hasImagekitPrivateKey ? "••••••••••••••••" : "Not configured"}
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-[#F3F2F2] flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[11px] font-bold text-[#706E6B] uppercase ">
                    <ShieldCheck size={14} className="text-[#2E844A]" /> AES-256 Cloud Encryption
                  </div>
                  <Button 
                    variant="brand" 
                    icon={Save} 
                    loading={loading} 
                    onClick={handleProfileUpdate}
                    className="px-8 shadow-sm shadow-blue-500/20"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* ACCOUNT MANAGEMENT SECTION */}
          {activeTab === 'account' && (
            <div className="space-y-6 animate-nebula-fade-in">
              <Card className="p-0 overflow-hidden">
                <div className="px-8 py-6 border-b border-[#F3F2F2] bg-[#F7F9FB]/50">
                  <h3 className="text-[16px] font-bold text-[#080707]">Two-Factor Authentication (2FA)</h3>
                  <p className="text-[12px] text-[#706E6B] mt-1">Add an extra layer of security to your account protocols.</p>
                </div>
                
                <div className="p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className={cn(
 "w-12 h-12 rounded-full flex items-center justify-center shrink-0",
 user?.twoFactorEnabled ? "bg-[#CDEFC4] text-[#2E844A]" : "bg-[#F3F2F2] text-[#706E6B]"
 )}>
                      <Shield size={24} />
                    </div>
                    <div>
                      <div className="text-[14px] font-bold text-[#080707]">
                        {user?.twoFactorEnabled ? '2FA is active' : '2FA is currently disabled'}
                      </div>
                      <p className="text-[12px] text-[#706E6B] mt-1 leading-relaxed max-w-md">
                        2FA helps prevent unauthorized access by requiring a dynamic code from your authenticator app.
                      </p>
                    </div>
                  </div>
                  {user?.twoFactorEnabled ? (
                    <Button variant="danger" outline icon={X} onClick={handleDisable2FA}>Disable 2FA</Button>
                  ) : (
                    <Button variant="brand" icon={QrCode} onClick={handleStart2FA}>Enable 2FA</Button>
                  )}
                </div>
              </Card>

              <Card className="p-0 overflow-hidden border-[#FDDDE3]">
                <div className="px-8 py-6 border-b border-[#FEF1EE] bg-[#FEF1EE]/30">
                  <h3 className="text-[16px] font-bold text-[#8E030F]">Danger Zone</h3>
                  <p className="text-[12px] text-[#BA0517]/70 mt-1">Actions that permanently affect your account.</p>
                </div>
                
                <div className="p-8 space-y-6">
                  <div className="flex items-start justify-between gap-6">
                    <div className="space-y-1">
                      <div className="text-[14px] font-bold text-[#080707]">Decommission Account</div>
                      <p className="text-[12px] text-[#706E6B] leading-relaxed max-w-xl">
                        Permanently purge your entire configuration registry, identity fragments, and history logs. This action is terminal and non-reversible.
                      </p>
                    </div>
                    <Button 
                      variant="danger" 
                      icon={Trash2} 
                      onClick={handleDeleteAccount}
                    >
                      Delete Account
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* 2FA SETUP MODAL */}
      <Modal 
        isOpen={is2FAModalOpen} 
        onClose={() => setIs2FAModalOpen(false)}
        title="Secure Multi-Factor Registration"
      >
        <div className="space-y-6 py-2">
          {twoFactorStep === 'qr' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center">
                <div className="p-4 bg-white border border-[#E5E5E5] rounded-lg mb-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <img src={qrCodeData.qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
                </div>
                <div className="text-center space-y-2">
                  <div className="text-[14px] font-bold text-[#080707]">Scan this code</div>
                  <p className="text-[12px] text-[#706E6B]">Scan this QR code with Google Authenticator or Microsoft Authenticator.</p>
                </div>
              </div>

              <div className="bg-[#F7F9FB] p-4 rounded-lg border border-[#F3F2F2]">
                <div className="flex items-center gap-4">
                   <div className="w-8 h-8 bg-white border border-[#E5E5E5] rounded-full flex items-center justify-center font-bold text-[13px]">1</div>
                   <div className="text-[13px] font-medium text-[#444444]">Open your authenticator app & scan.</div>
                </div>
                <div className="flex items-center gap-4 mt-3">
                   <div className="w-8 h-8 bg-white border border-[#E5E5E5] rounded-full flex items-center justify-center font-bold text-[13px]">2</div>
                   <div className="text-[13px] font-medium text-[#444444]">Enter the 6-digit code shown in the app.</div>
                </div>
              </div>

              <div className="pt-4 space-y-4">
                <Input 
                  placeholder="000000" 
                  value={verificationCode} 
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  className="text-center text-xl font-mono h-14"
                />
                <Button 
                  className="w-full h-12" 
                  variant="brand" 
                  disabled={verificationCode.length !== 6 || loading}
                  loading={loading}
                  onClick={handleVerifyAndEnable2FA}
                >
                  Verify and Activate
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 2FA DISABLE MODAL */}
      <Modal 
        isOpen={isDisabling2FA} 
        onClose={() => setIsDisabling2FA(false)}
        title="Disable Authentication Gateway"
      >
        <div className="space-y-6 py-2">
          <div className="p-4 bg-[#FEF1E5] border border-[#FDE4C9] rounded-lg flex items-start gap-3">
            <AlertCircle size={18} className="text-[#DD7A01] shrink-0" />
            <p className="text-[12px] text-[#A96404] leading-relaxed">
              Disabling 2FA reduces your account security. This operation requires your master password and a valid MFA token.
            </p>
          </div>

          <div className="space-y-4">
            <Input 
              label="Master Password"
              type="password"
              value={passwords.current}
              onChange={e => setPasswords({...passwords, current: e.target.value})}
            />
            <Input 
              label="Current MFA Code"
              placeholder="000000"
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value.replace(/\D/g,'').slice(0,6))}
              className="text-center text-xl font-mono"
            />
            <div className="pt-4 flex gap-3">
               <Button variant="outline" className="flex-1" onClick={() => setIsDisabling2FA(false)}>Cancel</Button>
               <Button 
                variant="danger" 
                className="flex-[2]" 
                disabled={!passwords.current || verificationCode.length !== 6}
                loading={loading}
                onClick={confirmDisable2FA}
               >
                 Confirm Deactivation
               </Button>
            </div>
          </div>
        </div>
      </Modal>

    </DashboardModuleShell>
  );
}
